import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import {
  type SubscriptionRow,
  type UserProfile,
  ensureUserProfileRow,
  fetchSubscription,
  fetchUsage,
  fetchUserProfile,
  freeUsesRemaining,
  hasFullAccess,
  incrementWorksheetUse,
  isAdminEmail,
  needsAgeCapture,
  needsParentalConsent,
  needsTosAcceptance,
} from '../lib/access';
import { recordAttributionIfNeeded } from '../lib/attribution';
import { type SchoolSeat, claimSchoolSeat, fetchIsSchoolAdmin, markSchoolSeatSeen } from '../lib/school';

interface AuthContextValue {
  loading: boolean;
  session: Session | null;
  user: User | null;
  subscription: SubscriptionRow | null;
  worksheetsUsed: number;
  freeUsesRemaining: number;
  isAdmin: boolean;
  hasAccess: boolean;
  profile: UserProfile | null;
  needsAgeCapture: boolean;
  needsParentalConsent: boolean;
  needsTosAcceptance: boolean;
  schoolSeat: SchoolSeat | null;
  isSchoolAdmin: boolean;
  dismissSchoolSeatBanner: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  refreshUsage: () => Promise<void>;
  recordWorksheetUse: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [sessionLoading, setSessionLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionRow | null>(null);
  const [worksheetsUsed, setWorksheetsUsed] = useState(0);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [schoolSeat, setSchoolSeat] = useState<SchoolSeat | null>(null);
  const [isSchoolAdmin, setIsSchoolAdmin] = useState(false);
  // User id whose subscription / usage / profile / school seat have finished their
  // first load. Until it matches the session, access is still being worked out and
  // the gates must not redirect (otherwise a paying or school-seated pupil past the
  // free-use cap flashes through to the paywall).
  const [accessLoadedFor, setAccessLoadedFor] = useState<string | null | undefined>(undefined);

  const refreshSubscription = useCallback(async () => {
    if (!session?.user) {
      setSubscription(null);
      return;
    }
    if (isAdminEmail(session.user.email)) {
      // Admins do not need a subscription row.
      setSubscription(null);
      return;
    }
    const row = await fetchSubscription(session.user.id);
    setSubscription(row);
  }, [session?.user]);

  const refreshUsage = useCallback(async () => {
    if (!session?.user) {
      setWorksheetsUsed(0);
      return;
    }
    if (isAdminEmail(session.user.email)) {
      setWorksheetsUsed(0);
      return;
    }
    const used = await fetchUsage(session.user.id);
    setWorksheetsUsed(used);
  }, [session?.user]);

  const recordWorksheetUse = useCallback(async () => {
    if (!session?.user) return;
    if (isAdminEmail(session.user.email)) return;
    const newCount = await incrementWorksheetUse(session.user.id);
    setWorksheetsUsed(newCount);
  }, [session?.user]);

  const refreshProfile = useCallback(async () => {
    if (!session?.user) {
      setProfile(null);
      return;
    }
    // First time we see this user, make sure a profile row exists.
    const row = await ensureUserProfileRow(session.user.id);
    setProfile(row ?? (await fetchUserProfile(session.user.id)));
  }, [session?.user]);

  // Claim any school seat invited to this email (and link SENCO admin rows).
  const refreshSchool = useCallback(async () => {
    if (!session?.user) {
      setSchoolSeat(null);
      setIsSchoolAdmin(false);
      return;
    }
    setSchoolSeat(await claimSchoolSeat());
    setIsSchoolAdmin(await fetchIsSchoolAdmin());
  }, [session?.user]);

  const dismissSchoolSeatBanner = useCallback(async () => {
    if (!schoolSeat) return;
    setSchoolSeat({ ...schoolSeat, seen_at: new Date().toISOString() });
    await markSchoolSeatSeen(schoolSeat.seat_id);
  }, [schoolSeat]);

  useEffect(() => {
    // Initial session check.
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setSessionLoading(false);
    });

    // Subscribe to changes (sign-in, sign-out, token refresh, magic-link callback).
    const { data: subscriptionListener } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => {
      subscriptionListener.subscription.unsubscribe();
    };
  }, []);

  // Whenever the session changes, refresh both the subscription row and the
  // free-use counter so the access gate has fresh data. Also: write the captured
  // attribution context to Supabase the first time we see this user.
  useEffect(() => {
    const uid = session?.user?.id ?? null;
    void Promise.all([refreshSubscription(), refreshUsage(), refreshProfile(), refreshSchool()]).then(() =>
      setAccessLoadedFor(uid),
    );
    if (uid) {
      void recordAttributionIfNeeded(uid);
    }
  }, [refreshSubscription, refreshUsage, refreshProfile, refreshSchool, session?.user?.id]);

  const signInWithMagicLink = useCallback(async (email: string) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return { error: 'Please enter an email address.' };
    const { error } = await supabase.auth.signInWithOtp({
      email: trimmed,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) return { error: error.message };
    return {};
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const user = session?.user ?? null;
  const accessLoading = sessionLoading || accessLoadedFor !== (user?.id ?? null);
  const isAdmin = isAdminEmail(user?.email);
  const hasAccess = hasFullAccess(session, subscription, worksheetsUsed, profile, schoolSeat, isSchoolAdmin);
  const remaining = freeUsesRemaining(worksheetsUsed);
  const ageCaptureNeeded = !!session?.user && !isAdmin && needsAgeCapture(profile);
  const consentNeeded = !!session?.user && !isAdmin && needsParentalConsent(profile);
  const tosAcceptanceNeeded = !!session?.user && !isAdmin && needsTosAcceptance(profile);

  const value = useMemo<AuthContextValue>(
    () => ({
      loading: accessLoading,
      session,
      user,
      subscription,
      worksheetsUsed,
      freeUsesRemaining: remaining,
      isAdmin,
      hasAccess,
      profile,
      needsAgeCapture: ageCaptureNeeded,
      needsParentalConsent: consentNeeded,
      needsTosAcceptance: tosAcceptanceNeeded,
      schoolSeat,
      isSchoolAdmin,
      dismissSchoolSeatBanner,
      refreshProfile,
      signInWithMagicLink,
      signOut,
      refreshSubscription,
      refreshUsage,
      recordWorksheetUse,
    }),
    [
      accessLoading,
      session,
      user,
      subscription,
      worksheetsUsed,
      remaining,
      isAdmin,
      hasAccess,
      profile,
      ageCaptureNeeded,
      consentNeeded,
      tosAcceptanceNeeded,
      schoolSeat,
      isSchoolAdmin,
      dismissSchoolSeatBanner,
      refreshProfile,
      signInWithMagicLink,
      signOut,
      refreshSubscription,
      refreshUsage,
      recordWorksheetUse,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}
